/* Zeus variant cards.
 *
 * Puts each option's own price on its tile, so the choice is legible without
 * selecting it first - the behaviour the Custom Cerakote Barbell page has.
 * That page does this inside zeus-colour-picker.js because it also owns the
 * colour grid; this is the standalone version for ordinary variant products.
 *
 * Reads the variant table emitted by zeus-variant-cards.liquid. Works for
 * multi-option products too: the price shown for a value is the variant you
 * would land on if you picked it while leaving the other options as they are.
 */
(() => {
  const root = document.querySelector('[data-zeus-vcards]');
  const dataEl = document.getElementById('zeus-vcards-data');
  if (!root || !dataEl) return;

  let data;
  try {
    data = JSON.parse(dataEl.textContent);
  } catch {
    return;
  }
  if (!data?.variants?.length) return;

  // Options listed in the product's custom.card_picker_delta metafield show the
  // price DIFFERENCE instead of the full configured total. On an add-on option
  // like Spotter Arms, the total (CA$1,495) reads as the add-on's price; the
  // difference (+$200) is what the customer actually wants to know.
  //
  // The difference is measured against the CHEAPEST choice in that option, not
  // the current selection - so the numbers stay put as the customer clicks
  // around instead of flipping sign. The cheapest choice reads 'Included'.
  const deltaOpts = new Set(String(data.delta || '').split(',').map((x) => x.trim().toLowerCase()).filter(Boolean));
  const isDelta = (index) => deltaOpts.has(String(data.options?.[index] || '').toLowerCase());
  const deltaLabel = (cents) => {
    if (cents <= 0) return 'Included';
    const whole = cents % 100 === 0;
    return '+$' + (whole ? (cents / 100).toLocaleString('en-CA') : (cents / 100).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  };

  // Some options choose a PART rather than the whole assembly: the Barbell tiles
  // on the Iso Rack Configurator must show the bar's own number (6940001.001),
  // not the rack variant's (3950017.001). custom.card_picker_sku maps an option
  // value to the number to print, as "Value = SKU" pairs. Prefix a pair with
  // "Option:" where two options share a value. Anything unmapped keeps showing
  // the variant's own SKU, so this changes nothing on products without it.
  const skuMap = new Map();
  for (const pair of String(data.skus || '').split(',')) {
    const i = pair.indexOf('=');
    if (i < 0) continue;
    const k = pair.slice(0, i).trim().toLowerCase();
    const v = pair.slice(i + 1).trim();
    if (k && v) skuMap.set(k, v);
  }
  // Options listed in custom.card_picker_nosku print no part number at all. On a
  // product where every option resolves to the same assembly - the Smith Machine,
  // where colour, crossmember and length all describe one SKU - repeating that
  // number on six tiles just restates what the page already shows once at the top.
  const noSku = new Set(String(data.nosku || '').split(',').map((x) => x.trim().toLowerCase()).filter(Boolean));
  const hideSku = (optName) => noSku.has(String(optName || '').trim().toLowerCase());

  const skuFor = (optName, value, fallback) =>
    skuMap.get((String(optName || '') + ':' + value).trim().toLowerCase()) ||
    skuMap.get(String(value).trim().toLowerCase()) || fallback;

  const money = (cents) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: data.currency || 'CAD' }).format(cents / 100);

  const fieldsets = () => [...root.querySelectorAll('.product-form__input--pill')];

  /* Which OPTION a control belongs to.
   *
   * Never assume the Nth pill fieldset is the Nth option. Dawn renders a colour
   * option as a swatch and a long option as a dropdown, and only the pills reach
   * fieldsets() - so on a product with Color as a swatch, pill #0 is option #1.
   * Everything was silently matched against the wrong option and no tile was
   * built at all. Dawn tags every input with data-option-name; use it, and fall
   * back to the legend text. */
  const optionIndexOfName = (name) =>
    data.options.findIndex((o) => String(o).trim() === String(name || '').trim());

  const optionIndexOf = (fs) => {
    const input = fs.querySelector('input[type="radio"]');
    let i = optionIndexOfName(input?.dataset.optionName);
    if (i >= 0) return i;
    const legend = fs.querySelector('legend, .form__label');
    i = optionIndexOfName(legend?.firstChild?.textContent);
    return i >= 0 ? i : null;
  };

  /* The value currently chosen in each option position. Read from EVERY control
   * in the picker, not just the pills, or the swatch's colour is missing from
   * the selection and the exact-match lookup never succeeds. */
  const selection = () => {
    const sel = new Array(data.options.length).fill(null);
    for (const input of root.querySelectorAll('.product-form__input input[type="radio"]')) {
      if (!input.checked) continue;
      const i = optionIndexOfName(input.dataset.optionName);
      if (i >= 0) sel[i] = input.value;
    }
    for (const select of root.querySelectorAll('.product-form__input select')) {
      const i = optionIndexOfName(select.dataset.optionName);
      if (i >= 0 && select.value) sel[i] = select.value;
    }
    return sel;
  };

  /* The variant you would land on by choosing `value` at `index`, holding the
     other options where they are. If that exact combination does not exist
     (ragged option grids), fall back to the cheapest variant carrying this
     value so the tile still says something true. */
  const variantFor = (index, value) => {
    const sel = selection();
    sel[index] = value;
    const exact = data.variants.find((v) => v.o.every((ov, i) => sel[i] == null || ov === sel[i]));
    if (exact) return exact;
    const any = data.variants.filter((v) => v.o[index] === value).sort((a, b) => a.p - b.p);
    return any.length ? any[0] : null;
  };

  const label = () => {
    fieldsets().forEach((fs) => {
      const index = optionIndexOf(fs);
      if (index == null) return;      // not an option we can place - leave Dawn's pills alone

      // An option whose values run long gets its own single column; the others
      // keep the two-column grid. Decided per option, not per product.
      const longest = Math.max(0, ...[...fs.querySelectorAll('input[type="radio"]')].map((i) => i.value.length));
      fs.classList.toggle('zeus-vcards__wide', longest > 28);

      // Echo the current choice beside the option name, so the whole configuration
      // can be confirmed from the three headings without re-reading the tiles.
      const legend = fs.querySelector('legend, .form__label');
      const chosen = fs.querySelector('input[type="radio"]:checked');
      if (legend && chosen) {
        let cur = legend.querySelector('.zeus-pill__current');
        if (!cur) { cur = document.createElement('span'); cur.className = 'zeus-pill__current'; legend.appendChild(cur); }
        cur.textContent = chosen.value;
      }
      for (const input of fs.querySelectorAll('input[type="radio"]')) {
        const el = document.querySelector('label[for="' + CSS.escape(input.id) + '"]');
        if (!el) continue;
        const match = variantFor(index, input.value);
        if (!match) continue;

        // Dawn puts a visually-hidden "Variant sold out or unavailable" span in
        // the label. Build the name from the input's own value and PRESERVE that
        // span rather than flattening textContent, which would swallow it into
        // the visible tile name.
        const hidden = el.querySelector('.label-unavailable');
        const name = document.createElement('span');
        name.className = 'zeus-pill__name';
        name.textContent = input.value;
        const price = document.createElement('span');
        price.className = 'zeus-pill__price';
        price.textContent = money(match.p);
        el.textContent = '';
        if (isDelta(index)) {
          const inputs = [...fs.querySelectorAll('input[type="radio"]')];
          const prices = inputs.map((i) => variantFor(index, i.value)?.p).filter((x) => x != null);
          const floor = prices.length ? Math.min(...prices) : match.p;
          price.textContent = deltaLabel(match.p - floor);
          price.classList.add('zeus-pill__price--delta');
        }
        el.append(name, price);

        // The part number, under the price, on EVERY tile - including options
        // priced as a difference. Delta pricing used to skip this, so a product
        // like the Squat Station showed no SKUs at all despite having one on all
        // 24 variants: whether a price reads "+$200" or "$895" has nothing to do
        // with whether the customer should see what they are ordering.
        //
        // Omitted when the variant has no SKU. A bundle parent whose components
        // are several different parts has no single number to show, and "SKU:
        // undefined" is worse than nothing.
        const shownSku = hideSku(data.options?.[index]) ? null : skuFor(data.options?.[index], input.value, match.s);
        if (shownSku) {
          const sku = document.createElement('span');
          sku.className = 'zeus-pill__sku';
          sku.textContent = 'SKU: ' + shownSku;
          el.append(sku);
        }
        if (hidden) el.appendChild(hidden);
      }
    });
  };

  // Dawn replaces the variant-picker markup on every change, wiping what we
  // injected - so re-label after each one. Capture, because the picker element
  // itself is swapped out.
  const section = root.closest('[data-section-id], section') || document;
  section.addEventListener('change', () => setTimeout(label, 60), true);
  label();
})();
